import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { PLATFORM_CONFIG } from './platformConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';

// Shared "add a platform mapping to an existing master product" modal — used both
// by each individual platform mapping page and by the Combined view (for adding a
// platform this product isn't mapped to yet, without leaving that page).
export default function AddMappingModal({ platform, product, stores, onClose, onSaved }) {
  const cfg = PLATFORM_CONFIG[platform];
  const [sku, setSku] = useState('');
  const [variationSku, setVariationSku] = useState('');
  const [storeId, setStoreId] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!sku.trim()) { toast.error(`Please enter ${cfg.skuLabel}`); return; }
    if (cfg.hasStores && !storeId) { toast.error(`Please select a ${cfg.label} store`); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/master-products/${product.id}/mappings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform,
          platform_sku: sku.trim(),
          variation_sku: variationSku.trim() || undefined,
          store_id: cfg.hasStores ? storeId : undefined,
        })
      });
      const data = await res.json();
      if (data.success) { toast.success('Mapping added!'); onSaved(); onClose(); }
      else toast.error(data.error);
    } catch { toast.error('Failed to add mapping'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Add {cfg.label} Mapping</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Mapping for: <strong className="text-gray-800">{product.product_name}</strong>
        </p>

        {cfg.hasStores && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">{cfg.label} Store</label>
            <select
              value={storeId}
              onChange={e => setStoreId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a store...</option>
              {(stores || []).map(s => (
                <option key={s.id} value={s.id}>{s.store_name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{cfg.skuLabel}</label>
          <input
            type="text"
            value={sku}
            onChange={e => setSku(e.target.value)}
            placeholder={cfg.skuPlaceholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {cfg.variationLabel && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">{cfg.variationLabel}</label>
            <input
              type="text"
              value={variationSku}
              onChange={e => setVariationSku(e.target.value)}
              placeholder={cfg.variationPlaceholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">{cfg.variationHint}</p>
          </div>
        )}

        {sku && (
          <div className="mb-6 px-3 py-2 rounded-lg text-sm font-mono bg-gray-100 text-gray-700 break-all">
            {sku}{variationSku ? ` / ${variationSku}` : ''}
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : 'Add Mapping'}
          </button>
        </div>
      </div>
    </div>
  );
}
